import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff, Mail, Phone } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { revealField } from '@/lib/passengers/api';
import { cn } from '@/lib/cn';
import { useContactRevision } from '@/lib/cases/contact';

// The group's contact ON FILE, mask-by-default (passenger-profile-completion).
//
// The roster only says WHETHER a phone / email exists (`contactOnFile` booleans);
// the value is served by the audited reveal route — the same audited path as the
// name, one click per field, never in the list by default. A party with nothing
// on file shows the two chips muted: that IS the counter-QR use case.

export function RevealableContact({
  caseUrn,
  paxUrn,
  contactOnFile,
  className,
  verifyCurrent = false,
}: {
  caseUrn: string;
  /** Any passenger of the party — the contact is group-level. */
  paxUrn: string;
  contactOnFile: { phone: boolean; email: boolean } | undefined;
  className?: string;
  /** Explicit verification in the editor reads through the audited endpoint,
   * even while the roster's presence-only snapshot is catching up. */
  verifyCurrent?: boolean;
}) {
  const revision = useContactRevision(caseUrn);
  return (
    <span className={cn('inline-flex flex-wrap items-center gap-1.5', className)} data-testid="pax-contact-on-file">
      {(['phone', 'email'] as const).map((field) => (
        <ContactChip key={`${caseUrn}:${paxUrn}:${field}:${contactOnFile?.[field]}:${revision}`}
          caseUrn={caseUrn} paxUrn={paxUrn} field={field}
          onFile={contactOnFile?.[field] === true} verifyCurrent={verifyCurrent} />
      ))}
    </span>
  );
}

function ContactChip({ caseUrn, paxUrn, field, onFile, verifyCurrent }: { caseUrn: string; paxUrn: string; field: 'phone' | 'email'; onFile: boolean; verifyCurrent: boolean }) {
  const { t } = useTranslation('pnr');
  const [revealed, setRevealed] = useState<string | null>(null);
  const reveal = useMutation({
    mutationFn: () => revealField(caseUrn, paxUrn, field),
    onSuccess: (r) => setRevealed(r.value),
  });
  const Icon = field === 'phone' ? Phone : Mail;
  const label = t(`profile.contact.${field}`);

  if (!onFile && !verifyCurrent) {
    return (
      <Badge variant="muted" size="sm" title={t('profile.contact.missingTitle', { field: label })} data-testid={`pax-contact-${field}-missing`}>
        <Icon className="mr-1 h-3 w-3" aria-hidden />
        {t('profile.contact.missing', { field: label })}
      </Badge>
    );
  }
  return (
    <Badge variant="info" size="sm" data-testid={`pax-contact-${field}`}>
      <Icon className="mr-1 h-3 w-3" aria-hidden />
      <span className="truncate">{revealed ?? (verifyCurrent ? label : t('profile.contact.onFile', { field: label }))}</span>
      {revealed !== null ? (
        <button type="button" onClick={() => setRevealed(null)} title={t('profile.contact.hide')} className="ml-1 text-muted-foreground hover:text-foreground" data-testid={`pax-hide-${field}`}>
          <EyeOff className="h-3 w-3" aria-hidden />
        </button>
      ) : (
        <button
          type="button"
          onClick={() => reveal.mutate()}
          disabled={reveal.isPending}
          title={t('profile.contact.reveal')}
          className="ml-1 text-muted-foreground hover:text-primary disabled:opacity-50"
          data-testid={`pax-reveal-${field}`}
        >
          <Eye className="h-3 w-3" aria-hidden />
        </button>
      )}
      {reveal.isError ? <span role="alert" className="ml-1 text-destructive">{t('profile.contact.failed')}</span> : null}
    </Badge>
  );
}
